import Swal from 'sweetalert2';

/**
 * Global Swal Utility Function
 * @param {Object} options - Configuration options for Swal
 * @param {string} options.title - The title of the alert
 * @param {string} options.text - The text content of the alert
 * @param {string} options.icon - Icon type: 'success', 'error', 'warning', 'info', 'question'
 * @param {string} options.confirmButtonText - Text for confirm button (default: 'OK')
 * @param {boolean} options.showCancelButton - Show cancel button (default: false)
 * @param {string} options.cancelButtonText - Text for cancel button (default: 'Cancel')
 * @param {string} options.width - Width of the modal (default: '450px')
 * @param {Function} options.onConfirm - Callback function when confirmed
 * @param {Function} options.onCancel - Callback function when cancelled
 */
export const showSwal = ({
    title = '',
    text = '',
    icon = 'info',
    confirmButtonText = 'OK',
    showCancelButton = false,
    cancelButtonText = 'Cancel',
    width = '450px',
    onConfirm = null,
    onCancel = null
} = {}) => {
    return Swal.fire({
        title,
        text,
        icon,
        confirmButtonText,
        showCancelButton,
        cancelButtonText,
        customClass: 'swal-wide',
        width,
        heightAuto: true
    }).then((result) => {
        if (result.isConfirmed && onConfirm) {
            onConfirm();
        } else if (result.isDismissed && onCancel) {
            onCancel();
        }
        return result;
    });
};

/**
 * Success Alert
 */
export const showSuccessSwal = (title = 'Success!', text = '') => {
    return showSwal({
        title,
        text,
        icon: 'success'
    });
};

/**
 * Error Alert
 */
export const showErrorSwal = (title = 'Error!', text = '') => {
    return showSwal({
        title,
        text,
        icon: 'error'
    });
};

/**
 * Warning Alert
 */
export const showWarningSwal = (title = 'Warning!', text = '') => {
    return showSwal({
        title,
        text,
        icon: 'warning'
    });
};

/**
 * Info Alert
 */
export const showInfoSwal = (title = 'Info', text = '') => {
    return showSwal({
        title,
        text,
        icon: 'info'
    });
};

/**
 * Pull a human-readable message out of an API error in whatever shape it
 * arrives. Covers:
 *   - axios errors: err.response.data.{ message | error | data.message | error.message | error.error_data.details }
 *   - hooks that pass the response body straight in: err.data.{ message | error }, err.{ message | error }
 *   - plain string errors
 *   - JS Error instances (err.message)
 * Falls back to the provided default text if nothing useful is found.
 */
export const extractApiErrorMessage = (err, fallback = 'Something went wrong') => {
    if (!err) return fallback;
    if (typeof err === 'string') return err;
    return (
        err?.response?.data?.message ||
        err?.response?.data?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.response?.data?.error?.error_data?.details ||
        (typeof err?.response?.data?.error === 'string' && err.response.data.error) ||
        err?.response?.data?.data?.error?.message ||
        (typeof err?.response?.data?.data?.error === 'string' && err.response.data.data.error) ||
        err?.response?.statusText ||
        err?.data?.message ||
        err?.data?.data?.message ||
        (typeof err?.data?.error === 'string' && err.data.error) ||
        err?.data?.error?.message ||
        (typeof err?.data?.data?.error === 'string' && err.data.data.error) ||
        err?.data?.data?.error?.message ||
        err?.message ||
        (typeof err?.error === 'string' && err.error) ||
        fallback
    );
};

/**
 * Pull a success message out of an API response in whatever shape it arrives.
 */
export const extractApiSuccessMessage = (response, fallback = 'Done') => {
    if (!response) return fallback;
    if (typeof response === 'string') return response;
    return (
        response?.data?.data?.message ||
        response?.data?.message ||
        response?.message ||
        fallback
    );
};

/**
 * Some APIs return HTTP 200 but signal failure inside the body
 * (e.g. `{ status: 500, error: "..." }` or `{ success: false, error: "..." }`).
 * Returns true when the response should be treated as an error.
 */
export const isApiResponseError = (response) => {
    if (!response) return false;
    if (typeof response === 'string') return false;

    const httpStatus = response?.status;
    if (typeof httpStatus === 'number' && httpStatus >= 400) return true;

    const candidates = [response?.data, response?.data?.data];
    for (const body of candidates) {
        if (!body || typeof body !== 'object') continue;

        const bodyStatus = body.status ?? body.statusCode ?? body.code;
        if (typeof bodyStatus === 'number' && bodyStatus >= 400) return true;

        if (body.success === false) return true;

        const errVal = body.error;
        if (typeof errVal === 'string' && errVal.trim()) return true;
        if (errVal && typeof errVal === 'object' && (errVal.message || errVal.error_data || Object.keys(errVal).length)) return true;
    }

    return false;
};

/**
 * Show an API error in a Swal, choosing the best message from any error shape.
 */
export const swalApiError = (err, fallback = 'Something went wrong') => {
    return showErrorSwal('Error', extractApiErrorMessage(err, fallback));
};

/**
 * Show an API success in a Swal, choosing the best message from any response shape.
 * If the response actually represents an error (e.g. HTTP 200 with body
 * `{ status: 500, error: "..." }`), show an error swal instead.
 */
export const swalApiSuccess = (response, fallback = 'Done') => {
    if (isApiResponseError(response)) {
        return swalApiError(response, fallback);
    }
    return showSuccessSwal('Success', extractApiSuccessMessage(response, fallback));
};

/**
 * Confirmation Dialog
 */
export const showConfirmSwal = ({
    title = 'Are you sure?',
    text = '',
    confirmButtonText = 'Yes',
    cancelButtonText = 'No',
    onConfirm = null,
    onCancel = null
} = {}) => {
    return showSwal({
        title,
        text,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        onConfirm,
        onCancel
    });
};